import {
	Button,
	Form,
	ValidationError,
	useMentionInputIntermediateState,
	MentionInputIntermediateStateHandlers
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubCommentMediaSaveModel,
	HubCommentModel,
	HubCommentSaveModel,
	UserMentionSaveModel,
	UserMentionViewModel
} from "client/Api/Hub/common";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { HubAvatar } from "../../HubAvatar/HubAvatar";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import styles from "./CommentForm.module.less";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	getModel,
	Mention,
	MentionData,
	MentionKind,
	TextWithMentions
} from "client/Components/MentionInput/converters";
import { isNil, isNotNil } from "client/Misc/utils";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { HubCommentSectionService } from "../HubCommentSection";
import { isProseMirror } from "client/Components/MentionInput/types";
import { CommentComposer, CommentComposerHandle } from "../Comment/CommentComposer";
import { HubCommentServerError, tryParseHubCommentServerError } from "../MobileComment/MobileCommentForm/MobileCommentForm";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";

export const EMPTY_COMMENT_FORM_DATA: CommentFormData = {
	message: {
		text: "",
		mentions: []
	},
	mentionData: [],
	media: { isSet: true, value: null }
};

export type CommentFormHandle = {
	focusInput(): void;
};

export type CommentFormProps = {
	service: HubCommentSectionService;
	currentUserInfo: UserIdentityModel;
	onCreated?: (comment: HubCommentModel) => void;
	options: HubCommentOptionsModel;
};

export const validationConfiguration: IValidationConfiguration<CommentFormData>
	= createValidationConfiguration<CommentFormData>();

validationConfiguration.when(
	x => isNil(x.media)
).forObject("message")
	.forScalar("text")
		.add(
			isNotNullNorUndefined()
				.activateOn("formSubmit")
				.validateOn("formSubmit"),
			isNotEmpty()
				.activateOn("formSubmit")
				.validateOn("formSubmit")
		);

export const CommentForm = forwardRef((props: CommentFormProps, ref: Ref<CommentFormHandle>): ReactElement => {
	const {
		service,
		currentUserInfo,
		onCreated,
		options
	} = props;

	const [formData, setFormData] = useState<CommentFormData>(EMPTY_COMMENT_FORM_DATA);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const commentComposerRef: RefObject<CommentComposerHandle | null> = useRef<CommentComposerHandle>(null);

	const intermediateState: MentionInputIntermediateStateHandlers = useMentionInputIntermediateState();

	const formHandler: IFormHandler<CommentFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formSubmitHandler = useCallback(
		async (): Promise<void> => {
			if (isSaving) {
				return;
			}

			setIsSaving(true);
			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsSaving(false);
					return;
				}

				const model: HubCommentSaveModel = createSaveModel(formData);

				const createCommentResult: HubCommentModel = await service.createComment(
					model,
					globalCancellation
				);

				onCreated?.(createCommentResult);
				setFormData(EMPTY_COMMENT_FORM_DATA);
				setIsSaving(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsSaving(false);

				if (formHandler.validation.tryProcessServerError(e)) {
					const result: TryResult<HubCommentServerError> = tryParseHubCommentServerError(e);
					if (!result.wasSuccessful) {
						return;
					}

					const mediaResult: string | undefined = result.result
						.media;

					if (isNotNil(mediaResult)) {
						alertManager.showError(mediaResult);

						formHandler.for("media")
							.reducer
							.setValue({
								isSet: true,
								value: null
							});

						formHandler.for("media")
							.validation
							.triggerValueChanged();
					}
				} else {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}
		},
		[
			alertManager,
			formData,
			globalCancellation,
			service,
			isSaving,
			formHandler,
			onCreated
		]
	);

	const isEmpty = useMemo(
		(): boolean => isProseMirror(intermediateState.intermediateState)
			&& getModel(intermediateState.intermediateState.editorState)
				.text
				.length === 0,
		[intermediateState]
	);

	const canSave = useMemo(
		(): boolean => {
			if (isSaving) {
				return false;
			}

			if (
				isEmpty
					&& formData.media.isSet
					&& formData.media.value === null
			) {
				return false;
			}

			return true;
		},
		[
			formData,
			isSaving,
			isEmpty
		]
	);

	useImperativeHandle(
		ref,
		() => ({
			focusInput(): void {
				commentComposerRef.current?.focus();
			}
		})
	);

	return (
		<div>
			<Form onSubmit={formSubmitHandler}>
				<div className={styles.formContent}>
					<HubAvatar
						authorUserInfo={currentUserInfo}
					/>
					<div className={styles.message}>
						<CommentComposer
							{...formHandler.for("message").attachToMentionInput(intermediateState, formData.mentionData)}
							ref={commentComposerRef}
							service={service}
							onFormDataChange={setFormData}
							formData={formData}
							placeholder={localize("_WriteYourComment")}
							trackingId="comment-composer"
							options={options}
						/>
						<ValidationError {...formHandler.for("message").attachToValidationError()} />
					</div>
					<div>
						<Button
							trackingId="post"
							size="large"
							kind="primary"
							isDisabled={!canSave}
							text={localize("_Post")}
							type="submit"
						/>
					</div>
				</div>
			</Form>
		</div>
	);
});

CommentForm.displayName = "Form";

export type CommentFormData = {
	message: TextWithMentions;
	mentionData: readonly MentionData[];
	media: OptionalModel<HubCommentMediaSaveModel | null>;
};

export function createFormDataForEdit(
	message: string,
	mentions: readonly UserMentionViewModel[]
): CommentFormData {
	return {
		media: { isSet: false },
		message: {
			text: message,
			mentions: mentions
				.map((x): Mention => ({
					kind: MentionKind.User,
					from: x.from,
					to: x.to,
					userId: x.user.userId
				}))
		},
		mentionData: mentions
			.map((x): MentionData => ({
				kind: MentionKind.User,
				userId: x.user.userId,
				displayName: getComputedDisplayName({
					firstName: x.user.firstName,
					lastName: x.user.lastName
				})
					.computedDisplayName
			}))
	};
}

export function createFormDataForReply(
	commentAuthor: UserIdentityModel,
	currentUserId: number
): CommentFormData {
	if (commentAuthor.userId === currentUserId) {
		return EMPTY_COMMENT_FORM_DATA;
	}

	const displayName: string = getComputedDisplayName({
		firstName: commentAuthor.firstName,
		lastName: commentAuthor.lastName
	})
		.computedDisplayName;

	return {
		message: {
			text: displayName + " ",
			mentions: [{
				kind: MentionKind.User,
				from: 0,
				to: displayName.length,
				userId: commentAuthor.userId
			}]
		},
		mentionData: [{
			kind: MentionKind.User,
			userId: commentAuthor.userId,
			displayName
		}],
		media: {
			isSet: true,
			value: null
		}
	};
}

export function createSaveModel(formData: CommentFormData): HubCommentSaveModel {
	return {
		message: formData.message
			.text,
		mentions: formData.message
			.mentions
			.map((x): UserMentionSaveModel => ({
				from: x.from,
				to: x.to,
				userId: x.userId
			})),
		media: formData.media
	};
}
